import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";
import type { EntitlementAccess } from "../../domain/access/entitlement";
import { observeAuth } from "../../infrastructure/firebase/auth-service";
import { observeEntitlement } from "../../infrastructure/firebase/entitlement-repository";

interface AuthContextValue {
  user: User | null;
  entitlement: EntitlementAccess;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  disabled = false,
}: {
  children: ReactNode;
  disabled?: boolean;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [entitlement, setEntitlement] = useState<EntitlementAccess>("free");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (disabled) {
      setUser(null);
      setEntitlement("free");
      setError(null);
      setLoading(false);
      return undefined;
    }
    let stopEntitlement: (() => void) | undefined;
    const stopAuth = observeAuth((nextUser) => {
      stopEntitlement?.();
      stopEntitlement = undefined;
      setUser(nextUser);
      setEntitlement("free");
      setError(null);
      if (nextUser) {
        stopEntitlement = observeEntitlement(
          nextUser.uid,
          (access) => {
            setEntitlement(access);
            setLoading(false);
          },
          (reason) => {
            setError(reason.message);
            setLoading(false);
          },
        );
      } else {
        setLoading(false);
      }
    });
    return () => {
      stopAuth();
      stopEntitlement?.();
    };
  }, [disabled]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    entitlement,
    loading,
    error,
  }), [entitlement, error, loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
